// ─────────────────────────────────────────────────────────────
// ZIFEK — Détection de la devise du visiteur
// Utilise une API de géolocalisation IP gratuite (ipwho.is) pour proposer
// la devise locale du visiteur. La devise du marchand reste la référence ;
// la devise détectée sert seulement de suggestion rapide.
// ─────────────────────────────────────────────────────────────

import { normalizeCurrency, isKnownCurrency } from '@/lib/currency';

const CACHE_KEY = 'zifek_geo_currency';
const TTL_MS = 6 * 60 * 60 * 1000; // 6 h

let detectionPromise: Promise<string | null> | null = null;

/** Délai réduit après un échec (quota atteint) : on ne re-tente pas sans cesse. */
const FAILURE_TTL_MS = 30 * 60 * 1000;

interface GeoCache {
  ts: number;
  currency: string | null;
  failed?: boolean;
}

/** Devise détectée précédemment (cache session, synchrone). */
export function getCachedGeoCurrency(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GeoCache;
    if (!parsed || Date.now() - parsed.ts > TTL_MS) return null;
    // Cache d'échec (quota API) : on garde le résultat « inconnu » un moment.
    if (parsed.failed && Date.now() - parsed.ts > FAILURE_TTL_MS) return null;
    return parsed.currency || null;
  } catch {
    return null;
  }
}

/**
 * Détecte la devise locale du visiteur à partir de son IP.
 * Renvoie un code devise supporté, ou null. Ne rejette jamais.
 */
export function detectVisitorCurrency(): Promise<string | null> {
  if (typeof fetch === 'undefined') return Promise.resolve(null);
  const cached = getCachedGeoCurrency();
  if (cached) return Promise.resolve(cached);
  if (detectionPromise) return detectionPromise;

  const store = (currency: string | null, failed: boolean) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), currency, failed }));
    } catch {
      /* stockage indisponible */
    }
  };

  detectionPromise = fetch('https://ipwho.is/')
    .then((res) => {
      // 429 / 5xx : quota ou service indisponible, on n'insiste pas.
      if (!res.ok) return null;
      return res.json();
    })
    .then((json) => {
      const rawCode = json?.currency?.code as string | undefined;
      const code = rawCode ? normalizeCurrency(rawCode) : null;
      const result = code && isKnownCurrency(code) ? code : null;
      store(result, !result);
      return result;
    })
    .catch(() => {
      store(null, true);
      return null;
    });

  return detectionPromise;
}